const state = {
  enabled: false,
  mode: "off",
  gtmId: "",
  ga4Id: "",
  gtmReady: false,
  ga4Ready: false,
};

const validModes = new Set(["gtm", "ga4", "both"]);

const normalizeText = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }
  return value.trim();
};

const resolveMode = (providerMode, gtmId, ga4Id) => {
  if (validModes.has(providerMode)) {
    return providerMode;
  }

  if (gtmId && ga4Id) {
    return "both";
  }
  if (gtmId) {
    return "gtm";
  }
  if (ga4Id) {
    return "ga4";
  }
  return "off";
};

const ensureDataLayer = () => {
  if (typeof window === "undefined") {
    return null;
  }

  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }

  return window.dataLayer;
};

const loadScript = (id, src) => {
  if (typeof document === "undefined") {
    return;
  }

  if (document.getElementById(id)) {
    return;
  }

  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

const initGtm = (gtmId) => {
  if (!gtmId || state.gtmReady) {
    return;
  }

  const dataLayer = ensureDataLayer();
  if (!dataLayer) {
    return;
  }

  dataLayer.push({
    "gtm.start": Date.now(),
    event: "gtm.js",
  });

  loadScript(
    `gtm-loader-${gtmId}`,
    `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`
  );

  state.gtmReady = true;
};

const initGa4 = (ga4Id) => {
  if (!ga4Id || state.ga4Ready) {
    return;
  }

  ensureDataLayer();

  loadScript(
    `ga4-loader-${ga4Id}`,
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4Id)}`
  );

  if (typeof window !== "undefined") {
    window.gtag =
      window.gtag ||
      function gtag() {
        window.dataLayer.push(arguments);
      };

    window.gtag("js", new Date());
    window.gtag("config", ga4Id, { send_page_view: false });
  }

  state.ga4Ready = true;
};

export const configureAnalytics = (settings = {}) => {
  const gtmId = normalizeText(settings.gtmId);
  const ga4Id = normalizeText(settings.ga4Id);
  const providerMode = normalizeText(settings.providerMode).toLowerCase();
  const mode = resolveMode(providerMode, gtmId, ga4Id);

  state.enabled = Boolean(settings.analyticsEnabled) && mode !== "off";
  state.mode = mode;
  state.gtmId = gtmId;
  state.ga4Id = ga4Id;

  if (!state.enabled) {
    return;
  }

  if ((mode === "gtm" || mode === "both") && gtmId) {
    initGtm(gtmId);
  }

  if ((mode === "ga4" || mode === "both") && ga4Id) {
    initGa4(ga4Id);
  }
};

const buildPageParams = ({ pathname, search, hash, title } = {}) => {
  const safePath = `${pathname || ""}${search || ""}${hash || ""}` || "/";
  const pageTitle = title || (typeof document !== "undefined" ? document.title : "");
  const pageLocation =
    typeof window !== "undefined"
      ? `${window.location.origin}${safePath}`
      : safePath;

  return {
    page_path: safePath,
    page_title: pageTitle,
    page_location: pageLocation,
  };
};

export const trackPageView = (pageData = {}) => {
  if (!state.enabled) {
    return;
  }

  const params = buildPageParams(pageData);

  if (state.gtmReady && Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: "page_view",
      ...params,
    });
  }

  if (state.ga4Ready && typeof window.gtag === "function") {
    window.gtag("event", "page_view", params);
  }
};

export const trackEvent = (eventName, params = {}) => {
  if (!state.enabled || !eventName) {
    return;
  }

  if (state.gtmReady && Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: eventName,
      ...params,
    });
  }

  if (state.ga4Ready && typeof window.gtag === "function") {
    window.gtag("event", eventName, params);
  }
};

export const toAnalyticsItem = (product, quantity = 1) => ({
  item_id: product?.slug || String(product?.id || ""),
  item_name: product?.title || "",
  item_category: product?.category || "General",
  price: Number(product?.price || 0),
  quantity: Number(quantity || 1),
});
